import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import Button from "../../../../../components/base/button/button";
import AddNewProductDialog from "../../../../../components/dialogs/add-new-product";
import {Add, ImportExportOutlined} from '@mui/icons-material';
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useIndustry, {useCachedIndustrySchemaController} from "../../../../../hooks/use-industry";
import {useDispatch} from "react-redux";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {Box, Typography} from "@mui/material";
import LookupSearchViewTopbarSearch from "../../lookup-v2/search/top-bar-search";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import Grid from "@mui/material/Unstable_Grid2";
import LookupSearchViewSidebarSearch from "../../lookup-v2/search/sidebar-search";
import LookupSearchViewProducts from "../../lookup-v2/search/products";
import {useHistory} from "react-router-dom";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import CacheService from "../../../../../../core/services/cache/cache_service";
import BroadcastService from "../../../../../../core/services/broadcast";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import ImportExternalProductDialog from "../../../../../components/dialogs/import-external-product";
import {usePartnerInformation} from "../../../../../hooks/use-partner-information";
import usePartnerDefaultNexusSearchOrderBy from "../../../../../hooks/use-partner-default-nexus-search-order-by";
import useQuery from "../../../../../hooks/use-query";

const initialSearchData = {
    industryId: undefined,
    keyword: '',
    filters: [],
    orderBy: undefined,
    isVisible: null,
    isPublicForBuyers: null,
}

/**
 * Parses a JSON query value, returning fallback on failure.
 * @param {unknown} value
 * @param {*} fallback
 */
const parseJsonQueryValue = (value, fallback) => {
    if (value == null || value === '') {
        return fallback;
    }
    if (typeof value !== 'string') {
        return value;
    }
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
};

/**
 * Coerces query-string boolean values to true | false | null.
 * @param {unknown} value
 * @returns {boolean|null}
 */
const parseNullableBoolean = (value) => {
    if (value === true || value === 'true') {
        return true;
    }
    if (value === false || value === 'false') {
        return false;
    }
    return null;
};

/**
 * Builds searchData fields from URL query params (products list only).
 * @param {Record<string, unknown>} query
 */
const searchDataFromQuery = (query) => {
    const filters = parseJsonQueryValue(query?.filters, []);
    const hasOrderBy = query?.orderBy != null && query?.orderBy !== '';
    const orderBy = hasOrderBy ? parseJsonQueryValue(query.orderBy, undefined) : undefined;
    return {
        industryId: query?.industryId || undefined,
        keyword: typeof query?.keyword === 'string' ? query.keyword : (query?.keyword ? String(query.keyword) : ''),
        filters: Array.isArray(filters) ? filters : [],
        orderBy: orderBy ?? undefined,
        isVisible: parseNullableBoolean(query?.isVisible),
        isPublicForBuyers: parseNullableBoolean(query?.isPublicForBuyers),
    };
};

/**
 * Serializes products-list search criteria for the URL (no pagination/facets).
 * @param {typeof initialSearchData} searchData
 * @param {boolean} includeOrderBy
 */
const queryFromSearchData = (searchData, includeOrderBy) => ({
    industryId: searchData.industryId || undefined,
    keyword: searchData.keyword || undefined,
    filters: searchData.filters?.length ? JSON.stringify(searchData.filters) : undefined,
    orderBy: includeOrderBy && searchData.orderBy != null
        ? JSON.stringify(searchData.orderBy)
        : undefined,
    isVisible: typeof searchData.isVisible === 'boolean' ? searchData.isVisible : undefined,
    isPublicForBuyers: typeof searchData.isPublicForBuyers === 'boolean' ? searchData.isPublicForBuyers : undefined,
});

const ProductsListView = () => {
    const history = useHistory();
    const [query, setQuery] = useQuery();
    const [featureFlags, setFeatureFlags] = useState(CacheService.getNexusSearchConfigs());
    const [addProductDialogState, setAddProductDialogState] = useState({open: false})
    const [importProductDialogState, setImportProductDialogState] = useState({open: false})
    const {getSchemaForIndustry} = useCachedIndustrySchemaController();
    const {cachedIndustry, loadingIndustries, selectIndustry, industries, hasNoIndustries} = useIndustry({noCategories: true});
    const [searchData, setSearchData] = useState(() => ({
        ...initialSearchData,
        ...searchDataFromQuery(query),
    }));
    const [holdOrderByForUrl, setHoldOrderByForUrl] = useState(
        () => query.orderBy != null && query.orderBy !== '',
    );
    const [searchSchema, setSearchSchema] = useState({entries: []});
    const [sidebarData, setSidebarData] = useState({searchFacets: []});
    const [loadingSearchSchema, setLoadingSearchSchema] = useState(true);
    const [advancedSearch, setAdvancedSearch] = useState(featureFlags.canToggleAdvancedMode);
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels()
    const partnerInformation = usePartnerInformation();
    const partnerId = useMemo(
        () => partnerInformation?.id ?? CacheService.getOperatorPartnerId(),
        [partnerInformation?.id],
    );
    const syncedIndustryIdRef = useRef(null);

    const {configureNotebookTags} = useConfigureNotebook('products-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * Sets the order by of the search data.
     * @type {(function(*): void)}
     */
    const setOrderByRaw = useCallback((orderBy) => {
        setSearchData(prevState => ({...prevState, orderBy: orderBy}));
    }, [])

    const {
        setOrderBy,
        resetOrderByToDefault,
        resetSortInteraction,
        markSortInteraction,
        isOrderByReady,
    } = usePartnerDefaultNexusSearchOrderBy({
        partnerId,
        searchSchema,
        loadingSearchSchema,
        orderBy: searchData.orderBy,
        setOrderBy: setOrderByRaw,
        isTokenLoading: holdOrderByForUrl,
    })

    /**
     * Preserves URL orderBy over partner default on first load.
     */
    useLayoutEffect(() => {
        if (!holdOrderByForUrl) {
            return;
        }
        markSortInteraction();
        setHoldOrderByForUrl(false);
    }, [holdOrderByForUrl, markSortInteraction]);

    const resultsReady = !loadingSearchSchema && isOrderByReady;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.productsList);
        configureBehavioralTool((tags) => tags.productsList);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * As soon as the component mounts:
     * - listens to the broadcast channel for the search configs.
     */
    useEffect(() => {
        const {close: bcClose} = BroadcastService.addEventListener(BroadcastService.channelNames.nexusSearchConfigs, 'message', setFeatureFlags)
        return () => {
            bcClose();
        }
    }, [setFeatureFlags])

    /**
     * Changes the industry with a confirmation dialog.
     * @type {(function(*, *): void)}
     */
    const onTabChanged = useCallback((e, industryId) => {
        const applyChanges = (industry) => {
            selectIndustry(industry);
            resetSortInteraction();
            setLoadingSearchSchema(true);
            setSearchData(({...initialSearchData, industryId: industry.id}))
            setSidebarData({searchFacets: []})
        }
        const industry = industries.find(e => e.id === industryId);
        if (
            !searchData.filters?.length &&
            !searchData.keyword &&
            typeof searchData.isVisible !== 'boolean' &&
            typeof searchData.isPublicForBuyers !== 'boolean'
        ) {
            return applyChanges(industry);
        }
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
            open: true,
            title: 'Change Industry',
            description: 'Are you sure you want to change the industry? this will reset the search filters and results',
            cancelButtonText: 'Cancel',
            confirmButtonText: 'Change',
            onClose: async (confirmed) => {
                if (confirmed) {
                    applyChanges(industry);
                }
                return true;
            }
        }));
    }, [selectIndustry, dispatch, industries, searchData.filters?.length, searchData.isPublicForBuyers, searchData.isVisible, searchData.keyword, resetSortInteraction])

    /**
     * Sets the default industryId of the search data with the cached industry id if it exists.
     * URL industryId (already in searchData) wins over the cache default.
     */
    useEffect(() => {
        setSearchData(prevState => {
            if (!loadingIndustries && !prevState.industryId && cachedIndustry) {
                return ({...prevState, industryId: cachedIndustry.id})
            }
            return prevState;
        })
    }, [loadingIndustries, cachedIndustry]);

    /**
     * Aligns the session industry cache with the industryId from searchData / URL.
     */
    useEffect(() => {
        if (loadingIndustries || !searchData.industryId || !industries?.length) {
            return;
        }
        if (syncedIndustryIdRef.current === searchData.industryId) {
            return;
        }
        const industry = industries.find(
            (e) => e.id === searchData.industryId || String(e.id) === String(searchData.industryId),
        );
        if (!industry) {
            return;
        }
        syncedIndustryIdRef.current = searchData.industryId;
        if (cachedIndustry?.id !== industry.id) {
            selectIndustry(industry);
        }
    }, [loadingIndustries, searchData.industryId, industries, cachedIndustry?.id, selectIndustry]);

    /**
     * Replace-syncs search criteria to the URL (no pagination / facets / searchToken).
     */
    useEffect(() => {
        setQuery(
            () => queryFromSearchData(searchData, isOrderByReady),
            {replace: true},
        );
    }, [
        searchData.industryId,
        searchData.keyword,
        searchData.filters,
        searchData.orderBy,
        searchData.isVisible,
        searchData.isPublicForBuyers,
        isOrderByReady,
        setQuery,
    ]);

    /**
     * Fetches the search schema
     * @type {(function(*): Promise<void>)}
     */
    const getSearchSchema = useCallback(async (industryId) => {
        setLoadingSearchSchema(true);
        const schema = await getSchemaForIndustry(industryId);
        if (!isMounted())
            return;
        setLoadingSearchSchema(false);
        if (schema) {
            setSearchSchema(schema);
        }
    }, [isMounted, getSchemaForIndustry])

    /**
     * As soon as the component mounts, fetch the search schema.
     */
    useEffect(() => {
        if (!searchData.industryId)
            return;
        void getSearchSchema(searchData.industryId);
    }, [getSearchSchema, searchData.industryId])
    /**
     * Sets the search facets
     * @type {(function(*): void)}
     */
    const setSearchFacets = useCallback((facets) => {
        setSidebarData(prevState => ({...prevState, searchFacets: facets}))
    }, [])

    /**
     * Sets the filters of the search data.
     * @type {(function(*): void)}
     */
    const setFilters = useCallback((filters) => {
        setSearchData(prevState => ({...prevState, filters: filters}));
    }, [])

    /**
     * Navigates to the product page with the product id.
     * @param product
     */
    const addNewProduct = useCallback((product) => {
        setAddProductDialogState({open: false});
        history.push(routeFunctions.products.single(product.id))
    }, [history])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', gap: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between'}}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        System Products
                    </Typography>
                    <Box sx={{flexGrow: 1}}/>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {
                            operatorAccessLevels.packages.lookup.importExternalProduct &&
                            <Button
                                className='d-flex align-items-center justify-content-center'
                                variant={'outlined'}
                                color={'primary'}
                                onClick={() => setImportProductDialogState({open: true})}
                            >
                                <ImportExportOutlined className={'mr-2'}/>
                                <p>Import External Product</p>
                            </Button>
                        }
                        {
                            operatorAccessLevels.packages.lookup.createNewProduct &&
                            <Button
                                className='d-flex align-items-center justify-content-center'
                                variant={'outlined'}
                                color={'primary'}
                                onClick={() => setAddProductDialogState({open: true})}
                            >
                                <Add className={'mr-2'}/>
                                <p>Add a new Product</p>
                            </Button>
                        }
                    </Box>
                </Box>
                {
                    industries.length > 1 &&
                    <Tabs value={searchData?.industryId ?? cachedIndustry?.id ?? false} onChange={onTabChanged}>
                        {
                            industries.map(industry => (
                                <Tab
                                    key={industry.id}
                                    value={industry.id}
                                    label={industry.title}
                                />
                            ))
                        }
                    </Tabs>
                }
                {
                    hasNoIndustries &&
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBlock: 12}}>
                        <Typography variant={'body2'} color={'text.secondary'} fontWeight={600} textAlign={'center'}>
                            No industries found. Please add an industry or contact your administrator.
                        </Typography>
                    </Box>
                }
                {
                    !hasNoIndustries &&
                    <>
                        <LookupSearchViewTopbarSearch
                            featureFlags={featureFlags}
                            loading={loadingSearchSchema || !isOrderByReady}
                            searchData={searchData}
                            setSearchData={setSearchData}
                            searchSchema={searchSchema}
                            includeProductListFilters
                            advancedSearch={advancedSearch}
                            setAdvancedSearch={setAdvancedSearch}
                            resetOrderByToDefault={resetOrderByToDefault}
                        />
                        {
                            !resultsReady &&
                            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBlock: 12}}>
                                <LoadingLottieAnimation width={'80%'}/>
                            </Box>
                        }
                        {
                            resultsReady &&
                            <Grid container>
                                <Grid xs={12} md={'auto'}>
                                    <Box sx={{width: {xs: '100%', md: 300}}}>
                                        <LookupSearchViewSidebarSearch
                                            featureFlags={featureFlags}
                                            searchSchema={searchSchema}
                                            facets={sidebarData.searchFacets}
                                            filters={searchData.filters}
                                            orderBy={searchData.orderBy}
                                            setOrderBy={setOrderBy}
                                            setFilters={setFilters}
                                        />
                                    </Box>
                                </Grid>
                                <Grid xs={12} md>
                                    <LookupSearchViewProducts
                                        inProductsList
                                        industries={industries}
                                        advancedSearch={advancedSearch && featureFlags.canToggleAdvancedMode}
                                        searchData={searchData}
                                        minRelevanceScore={searchSchema.minRelevanceScore ?? 0}
                                        setFacets={setSearchFacets}
                                        searchSchema={searchSchema}
                                        setSearchData={setSearchData}
                                        getScrollParent={() => document.querySelector('main')}
                                    />
                                </Grid>
                            </Grid>
                        }
                    </>
                }
            </Box>
            {
                operatorAccessLevels.packages.lookup.createNewProduct &&
                <AddNewProductDialog
                    open={addProductDialogState.open}
                    setOpen={() => setAddProductDialogState({open: false})}
                    onSuccess={addNewProduct}
                />
            }
            {
                operatorAccessLevels.packages.lookup.importExternalProduct &&
                <ImportExternalProductDialog
                    open={importProductDialogState.open}
                    setOpen={() => setImportProductDialogState({open: false})}
                    onSuccess={addNewProduct}
                />
            }
        </>
    )
}

export default ProductsListView;
